import Cookies from 'js-cookie';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { getBscGlobalState, sendPackageOperationalEvent } from '../../../common/utils';
import { Logger } from '../../../common/utils/logger';

/**
 * Sets a cookie synchronously. Since this doesn't wait for user preferences,
 * it should only be used for strictly necessary cookies which are critical to
 * application functionality.
 *
 * @param attributes - Forwarded as-is to js-cookie's `Cookies.set`. The `CookieAttributes` type is
 *   broader than what js-cookie actually serializes: its index signature accepts arbitrary
 *   keys/values, but js-cookie only handles `expires` (`number | Date`), string-valued attributes
 *   (`path`, `domain`, …) and boolean flags (`secure`). A non-string value under any other key
 *   makes js-cookie throw while serializing. This function does not coerce attribute values.
 *
 * @throws Re-throws any error from `Cookies.set` (e.g. an invalid attribute value) after recording a
 *   `usedSetStrictlyNecessaryCookieError` operational event, so callers passing invalid attributes
 *   fail fast.
 */
export const setStrictlyNecessaryCookie = (
	key: string,
	value: string,
	attributes?: Cookies.CookieAttributes,
): void => {
	if (fg('platform_moonjelly_bsc_master_killswitch')) {
		// Set the cookie without package operational events
		// when master killswitch is enabled
		Cookies.set(key, value, { ...attributes, 'atl-set-cookie': true });
	} else {
		try {
			if (!getBscGlobalState().hasInitialized) {
				sendPackageOperationalEvent({
					action: 'setItemWithoutInit',
					attributes: { itemKey: key, storageType: 'Cookie' },
				});
			}

			sendPackageOperationalEvent({
				action: 'usedSetStrictlyNecessaryCookie',
				attributes: {
					cookieKey: key,
				},
			});
			Cookies.set(key, value, { ...attributes, 'atl-set-cookie': true });
		} catch (e: any) {
			Logger.errorWithOperationalEvent({
				action: 'usedSetStrictlyNecessaryCookieError',
				attributes: { cookieKey: key },
				message: `Failed to use set strictly necessary cookie. ${e.message || ''}`,
			});
			throw e;
		}
	}
};
